interface ConvertKeysConfig<T> {
  obj: T;
  convertFn(key: string | number): string;
  deep?: boolean;
}

const isObject = (value: unknown): boolean =>
  value === Object(value) && typeof value !== 'function';

export const convertKeysToCase = <Input, Output>({
  obj,
  convertFn,
  deep = false,
}: ConvertKeysConfig<Input>): Output => {
  if (typeof convertFn !== 'function') {
    throw new Error('convertKeysCase: convertFn must be a function');
  }

  const getObjectValue = (value: Input[keyof Input]) => {
    const convertDeepValue = deep && isObject(value);
    return convertDeepValue ? convertKeysToCase({ obj: value, convertFn, deep }) : value;
  };

  // casting to any here because when we get the array value it might not Input[keyof Input] anymore in deeply nested objection.
  // this is because the array item might be deep and when that happens the type is not know to us anymore
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getArrayValue = (value: any) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value.map((x: any) => {
      // If we have an array of object we want to convert its keys, otherwise we want to skip it for primitive values.
      if (typeof x === 'object') {
        return convertKeysToCase({ obj: x, convertFn, deep });
      }

      return x;
    });

  if (Array.isArray(obj)) {
    return getArrayValue(obj);
  }

  if (obj === null || obj === undefined) {
    return {} as Output;
  }

  return Object.entries(obj).reduce((result, [key, value]) => {
    // eslint-disable-next-line no-param-reassign
    result[convertFn(key) as keyof Output] = Array.isArray(value) // nosemgrep
      ? getArrayValue(value)
      : getObjectValue(value);

    return result;
  }, {} as Output);
};
